import { useCallback, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'

/**
 * Habilitations du back-office.
 *
 * Le rôle était testé par un `includes()` sur trois valeurs, et les onze
 * pages étaient protégées à l'identique : un préparateur promu administrateur
 * voyait le chiffre d'affaires et pouvait changer les rôles.
 *
 * Important : ceci masque, ça n'autorise pas. Le serveur réévalue chaque
 * capacité à chaque appel (middleware `can:`). Un utilisateur qui modifierait
 * son localStorage verrait apparaître des boutons dont chaque action serait
 * refusée en 403.
 */
export function useCan() {
  const { user } = useAuth()

  const capabilities = useMemo(
    () => new Set(user?.capabilities ?? []),
    [user?.capabilities]
  )

  /** can('orders.write') — ou can('orders.write', 'orders.export') pour un OU. */
  const can = useCallback(
    (...required) => required.some(c => capabilities.has(c)),
    [capabilities]
  )

  /** Exige toutes les capacités listées. */
  const canAll = useCallback(
    (...required) => required.every(c => capabilities.has(c)),
    [capabilities]
  )

  return {
    can,
    canAll,
    capabilities,
    isStaff: !!user?.is_staff,
    role: user?.role ?? null,
  }
}

/**
 * Masque ses enfants si la capacité manque.
 *
 *   <Can do="products.delete">
 *     <button>Supprimer</button>
 *   </Can>
 *
 * `fallback` permet d'afficher un état désactivé plutôt que rien, ce qui est
 * souvent préférable : un bouton absent laisse croire à un bug, un bouton
 * grisé explique.
 */
export function Can({ do: required, any, children, fallback = null }) {
  const { can } = useCan()
  const list = any ?? (Array.isArray(required) ? required : [required])

  return can(...list) ? children : fallback
}
